import { useMyFinancialStatementById } from "api/Client/balance";
import { Spinner } from "components";
import { image_host } from "config/api_host";
import {
  HiOutlineBanknotes,
  HiOutlineCalendarDays,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineHashtag,
  HiOutlineReceiptRefund,
  HiOutlineShoppingBag,
  HiOutlineWallet,
  HiOutlineXCircle,
} from "react-icons/hi2";
import ClientActionModal from "../ClientActionModal/ClientActionModal";

const openImageInPopup = (url, title, width = 600, height = 400) => {
  const left = (window.screen.width - width) / 2;
  const top = (window.screen.height - height) / 2;
  const newWindow = window.open(
    "",
    title,
    `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`
  );

  if (newWindow) {
    newWindow.document.write(`
      <html>
        <head><title>${title}</title></head>
        <body style="margin:0;padding:0;background:#2a1200;display:grid;place-items:center;">
          <img src="${url}" style="width:100%;height:100%;object-fit:contain;" />
        </body>
      </html>
    `);
    newWindow.document.close();
  }
};

const getStateMeta = (state) => {
  if (state === "pending") {
    return { label: "انتظار", icon: <HiOutlineClock />, color: "text-amber-400" };
  }
  if (state === "approved" || state === "completed") {
    return { label: "مكتملة", icon: <HiOutlineCheckCircle />, color: "text-emerald-400" };
  }
  return { label: "مرفوضة", icon: <HiOutlineXCircle />, color: "text-rose-400" };
};

const getTypeMeta = (type) => {
  switch (type) {
    case "bankNotice":
      return { label: "إشعار بنكي", icon: <HiOutlineBanknotes className="text-orange-400" /> };
    case "balanceRecharge":
      return { label: "شحن رصيد", icon: <HiOutlineWallet className="text-emerald-400" /> };
    case "transaction":
      return { label: "إرسال رصيد", icon: <HiOutlineReceiptRefund className="text-orange-300" /> };
    case "refund":
      return { label: "مرتجع", icon: <HiOutlineReceiptRefund className="text-amber-400" /> };
    case "purchaseInvoice":
      return { label: "فاتورة شراء", icon: <HiOutlineShoppingBag className="text-amber-400" /> };
    default:
      return { label: "- - -", icon: <HiOutlineReceiptRefund className="text-slate-400" /> };
  }
};

export default function MyFinancialStatementModal({ isOpen, toggle, financialId }) {
  const {
    data: currentOrder,
    isSuccess: currentOrderIsSuccess,
    isFetching: currentOrderIsFetching,
  } = useMyFinancialStatementById(financialId);

  return (
    <ClientActionModal open={isOpen} close={toggle} size="small" title="تفاصيل العملية">
      {currentOrderIsFetching ? (
        <div className="portal-page-card mx-auto flex items-center justify-center rounded-[1.35rem] p-6">
          <Spinner sm />
        </div>
      ) : (
        currentOrderIsSuccess && (
          <div className="grid grid-cols-1 gap-3">
            <div className="portal-page-card grid gap-3 rounded-[1.35rem] p-5">
              <div className="flex items-center justify-between gap-3">
                <div
                  className={`flex items-center gap-2 text-sm font-bold ${
                    getStateMeta(currentOrder.data.order_state).color
                  }`}
                >
                  <span className="text-xl">
                    {getStateMeta(currentOrder.data.order_state).icon}
                  </span>
                  {getStateMeta(currentOrder.data.order_state).label}
                </div>
                <h2 className="text-xl font-bold portal-accent" style={{ direction: "ltr" }}>
                  {Number(Math.abs(currentOrder.data.amount)).toLocaleString()}{" "}
                  <span className="text-sm">
                    {currentOrder.data.order_type === "bankNotice"
                      ? currentOrder.data.currency_bank
                      : currentOrder.data.currency_customer}
                  </span>
                </h2>
              </div>

              <InfoRow icon={getTypeMeta(currentOrder.data.order_type).icon} label="نوع العملية" value={getTypeMeta(currentOrder.data.order_type).label} />
              <InfoRow icon={<HiOutlineHashtag className="text-amber-400" />} label="المعرّف" value={currentOrder.data.uuid} />
              <InfoRow icon={<HiOutlineShoppingBag className="text-orange-300" />} label="الوصف" value={currentOrder.data.description} />
              <InfoRow icon={<HiOutlineCalendarDays className="text-orange-400" />} label="التاريخ" value={currentOrder.data.created_at} ltr />
              {currentOrder.data.order_type === "bankNotice" ? (
                <InfoRow icon={<HiOutlineWallet className="text-emerald-400" />} label="الرصيد المضاف" value={`${Number(currentOrder.data.total_amount).toLocaleString()} ${currentOrder.data.currency_customer}`} ltr />
              ) : null}
              <InfoRow icon={<HiOutlineBanknotes className="text-emerald-400" />} label="الرصيد قبل العملية" value={`${Number(currentOrder.data.total_before_operation).toLocaleString()} ${currentOrder.data.currency_customer}`} ltr />
            </div>

            {!isEmpty(currentOrder.data.order_fields) ? (
              <div className="portal-panel-soft rounded-[1.35rem] p-5">
                <h5 className="mb-4 text-lg font-bold portal-accent">معلومات الطلب</h5>
                <div className="mb-2 grid grid-cols-1 gap-6 md:grid-cols-3">
                  {Object.entries(currentOrder.data.order_fields).map(([key, item]) => (
                    <div className="flex flex-col gap-1" key={key}>
                      <span className="text-sm font-semibold portal-accent">{item.name}</span>
                      <span className="text-lg font-bold" style={{ color: "var(--layout-text)" }}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {currentOrder.data.rejection_reason || currentOrder.data.reason ? (
              <div className="portal-panel-soft rounded-[1.35rem] p-5">
                <h5 className="mb-4 text-lg font-bold portal-accent">
                  {currentOrder.data.order_state === "rejected" ||
                  currentOrder.data.order_state === "canceled"
                    ? "سبب الرفض"
                    : "الملاحظات"}
                </h5>
                <div
                  className="break-words font-bold leading-7"
                  style={{ color: "var(--layout-text)" }}
                  dangerouslySetInnerHTML={{
                    __html: (
                      currentOrder.data.rejection_reason ||
                      currentOrder.data.reason ||
                      ""
                    ).replace(/\n/g, "<br />"),
                  }}
                />
              </div>
            ) : null}

            {currentOrder.data.notify_image ? (
              <div className="portal-panel-soft rounded-[1.35rem] p-5">
                <h5 className="mb-4 text-lg font-bold portal-accent">صورة الإشعار</h5>
                <img
                  onClick={() =>
                    openImageInPopup(
                      `${image_host}${currentOrder.data.notify_image}`,
                      `المركز: ${currentOrder.data.center_name} | رقم الإشعار: ${currentOrder.data.notify_number} |`,
                      800,
                      600
                    )
                  }
                  src={`${image_host}${currentOrder.data.notify_image}`}
                  alt="صورة الإشعار"
                  className="mx-auto max-h-[420px] w-full cursor-pointer rounded-2xl object-contain"
                />
              </div>
            ) : null}
          </div>
        )
      )}
    </ClientActionModal>
  );
}

function InfoRow({ icon, label, value, ltr = false }) {
  return (
    <div className="flex items-start gap-3" style={{ color: "var(--layout-text-muted)" }}>
      <div className="text-xl">{icon}</div>
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-xs font-semibold portal-accent">{label}</span>
        <span className="break-all font-bold" style={{ direction: ltr ? "ltr" : "rtl", color: "var(--layout-text)" }}>
          {value}
        </span>
      </div>
    </div>
  );
}

function isEmpty(field) {
  if (field === undefined || field === null) return true;
  if (typeof field === "object") return Object.keys(field).length === 0;
  if (Array.isArray(field)) return field.length === 0;
  return false;
}
